import { useState, useEffect } from "react";
import { T, STATE, MARKET, STOCK_COLORS, canAccess, getStateConfig } from "../../config/theme.js";
import { api, trackEvent } from "../../hooks/useApi.js";
import { Card, Btn, Bar, StateBadge, SectionTitle, Input } from "../ui/index.jsx";
import StockSearch from "./StockSearch.jsx";
import ActionModal from "../modals/ActionModal.jsx";
import { Zap, Activity, AlertTriangle, CheckCircle, Target, Info, RefreshCw, Plus, PieChart, TrendingUp, TrendingDown, Lock, Flame, BarChart2, MessageCircle } from "lucide-react";
import { dashboardCardTemplates, selectTemplate } from "../../config/messageTemplates.js";
import InstallBanner from "../ui/InstallBanner.jsx";
import PushNotificationBanner from "../ui/PushNotificationBanner.jsx";
import PrincipleDriftCard from "../ui/PrincipleDriftCard.jsx";
import { Eye, Hourglass } from "lucide-react";

export default function Dashboard({ token, plan, goDetail, goQA, goJourney, goMarketQA, goMonthly, goDividend, onUpgrade }) {
  const [holdings, setHoldings] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [reports, setReports] = useState({});
  const [watchReports, setWatchReports] = useState({});
  const [briefing, setBriefing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [addMode, setAddMode] = useState("holding"); // "holding" | "watchlist"
  const [form, setForm] = useState({ symbol: "", name: "", avg_price: "", qty: "", period: "long_term", strategy: "general" });
  const [watchForm, setWatchForm] = useState({ symbol: "", name: "", watch_reason: "", entry_conditions: "", target_price: "" });
  const [generating, setGenerating] = useState(false);
  const [reportCreating, setReportCreating] = useState(null); // { name, symbol, holdingData } — 자동 리포트 생성 중
  const [actionModal, setActionModal] = useState(null); // { holding, report }
  const [stockFilter, setStockFilter] = useState("all"); // "all" | "holding" | "watchlist"

  // 마이크로 피드백 배너 (IIFE에서 컴포넌트 최상단으로 이동)
  const feedbackToday = new Date().toISOString().slice(0, 10);
  const feedbackDismissedKey = `pause_feedback_dismissed_${feedbackToday}`;
  const [feedbackDismissed, setFeedbackDismissed] = useState(() => !!localStorage.getItem(feedbackDismissedKey));
  const [feedbackVisible, setFeedbackVisible] = useState(false);

  // W6: 연속 확인 스트릭
  const [streak, setStreak] = useState(0);
  useEffect(() => {
    trackEvent("dashboard.view"); // 텔레메트리: 대시보드 조회
    const today = new Date().toISOString().slice(0, 10);
    const streakData = JSON.parse(localStorage.getItem("phase_streak") || '{"dates":[],"count":0}');
    if (!streakData.dates.includes(today)) {
      streakData.dates.push(today);
      // 연속 체크: 어제도 접속했는지
      const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
      if (streakData.dates.includes(yesterday)) {
        streakData.count = (streakData.count || 0) + 1;
      } else {
        streakData.count = 1;
      }
      // 최근 60일만 보관
      streakData.dates = streakData.dates.slice(-60);
      localStorage.setItem("phase_streak", JSON.stringify(streakData));
    }
    setStreak(streakData.count || 1);
  }, []);

  const load = async () => {
    try {
      const h = await api("/holdings", {}, token); setHoldings(h);
      const r = await api("/reports?limit=10", {}, token);
      const map = {}; (Array.isArray(r) ? r : [r]).forEach(rep => { if (rep.symbol && !map[rep.symbol]) map[rep.symbol] = rep; }); setReports(map);
      // 대기종목 + 리포트 조회 (Sprint 22)
      try {
        const w = await api("/watchlist", {}, token);
        setWatchlist(w?.items || []);
        // 대기종목 리포트도 reports에서 조회 (asset_type=watchlist)
        const wr = await api("/reports?asset_type=watchlist&limit=20", {}, token);
        const wMap = {}; (Array.isArray(wr) ? wr : []).forEach(rep => { if (rep.symbol && !wMap[rep.symbol]) wMap[rep.symbol] = rep; }); setWatchReports(wMap);
      } catch(e) { console.log("watchlist load:", e.message); }
      // 일일 브리핑 로드
      try { const b = await api("/briefing", {}, token); setBriefing(b); } catch(e) { console.log("briefing not available yet"); }
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  // 마이크로 피드백 배너 useEffect (최상단)
  useEffect(() => {
    if (feedbackDismissed) return;
    const holdRecords = holdings.filter(h => {
      const yKey = `pause_active_hold_${h.symbol}_${new Date(Date.now() - 86400000).toISOString().slice(0, 10)}`;
      return localStorage.getItem(yKey);
    });
    if (holdRecords.length > 0) {
      const h = holdRecords[0];
      const r = reports[h.symbol];
      if (r?.micro?.pnl_1d != null && r.micro.pnl_1d < 0) {
        setFeedbackVisible(true);
        const timer = setTimeout(() => setFeedbackVisible(false), 4000);
        return () => clearTimeout(timer);
      }
    }
  }, [feedbackDismissed, holdings, reports]);

  const add = async () => {
    if (!form.symbol || !form.name) return alert("종목을 검색해서 선택하세요");
    if (!form.avg_price || !form.qty) return alert("매수 가격과 수량을 입력하세요");

    // 같은 종목이 이미 있으면 분할매수 처리 (평균단가 재계산)
    const existing = holdings.find(h => h.symbol === form.symbol);
    if (existing) {
      const oldTotal = existing.avg_price * existing.qty;
      const newTotal = (+form.avg_price) * (+form.qty);
      const totalQty = existing.qty + (+form.qty);
      const newAvg = Math.round((oldTotal + newTotal) / totalQty);

      // 기존 삭제 후 새로 등록
      try {
        await api(`/holdings/${existing.id}`, { method: "DELETE" }, token);
        await api("/holdings", { method: "POST", body: JSON.stringify({ symbol: form.symbol, name: form.name, avg_price: newAvg, qty: totalQty }) }, token);
        setForm({ symbol: "", name: "", avg_price: "", qty: "", period: "long_term", strategy: "general" }); setAdding(false); load();

        // 분할매수 기록도 저장
        const addBuyRecord = {
          symbol: form.symbol, name: form.name, action_type: "add_buy",
          avg_price: +form.avg_price, buy_qty: +form.qty,
          new_avg_price: newAvg, new_total_qty: totalQty,
          phase_state: reports[form.symbol]?.state_stock || "WATCH",
          alignment: "personal", memo: `분할매수: ${(+form.qty)}주 × ${(+form.avg_price).toLocaleString()}원 → 평단가 ${newAvg.toLocaleString()}원`,
        };
        saveAction(addBuyRecord);
      } catch (e) { alert(e.message); }
    } else {
      try {
        const result = await api("/holdings", { method: "POST", body: JSON.stringify({ symbol: form.symbol, name: form.name, avg_price: +form.avg_price, qty: +form.qty, prefs: { investment_period: form.period, investment_strategy: form.strategy } }) }, token);
        trackEvent("holding.add", { symbol: form.symbol });
        const savedName = form.name;
        const savedSymbol = form.symbol;
        setForm({ symbol: "", name: "", avg_price: "", qty: "", period: "long_term", strategy: "general" }); setAdding(false);
        
        // 자동 리포트 생성 중 표시
        setReportCreating({ name: savedName, symbol: savedSymbol, holdingData: result, done: false });
        
        // 리포트 생성 완료 대기 (백엔드에서 이미 생성됨)
        await load();
        setReportCreating(prev => prev ? { ...prev, done: true } : null);
      } catch (e) { alert(e.message); }
    }
  };

  const del = async (id, silent = false) => { if (!silent && !confirm("삭제할까요?")) return; try { await api(`/holdings/${id}`, { method: "DELETE" }, token); trackEvent("holding.delete"); load(); } catch (e) { if (!silent) alert(e.message); } };

  const generate = async () => { setGenerating(true); try { await api("/test/report/am", { method: "POST" }, token); await load(); } catch (e) { alert(e.message); } setGenerating(false); };

  const saveAction = (actionData) => {
    api("/actions", { method: "POST", body: JSON.stringify(actionData) }, token)
      .catch(() => {
        // fallback: localStorage
        const saved = JSON.parse(localStorage.getItem("phase_actions") || "[]");
        saved.unshift(actionData);
        localStorage.setItem("phase_actions", JSON.stringify(saved));
      });
  };

  // Sprint 20: 새 키(STABLE/CHANGE/VOLATILE/CHECKNEEDED) + 기존 키 하위호환
  const _normState = (s) => ({ HOLD:"STABLE", WATCH:"CHANGE", CAUTION:"VOLATILE", PAUSE:"CHECKNEEDED" }[s] || s || "CHANGE");
  const counts = { STABLE: 0, CHANGE: 0, VOLATILE: 0, CHECKNEEDED: 0 };
  holdings.forEach(h => { const r = reports[h.symbol]; if (r) { const k = _normState(r.state_stock); counts[k] = (counts[k] || 0) + 1; } });

  // ═══ Sprint 24: 종합 해설 (행동 지시 X, 상태 서술만) ═══
  // 보유 종목 전체 상태를 엮어서 "오늘 내 상황"을 한눈에 읽어주는 Level 1 해설
  const getOverview = () => {
    if (holdings.length === 0) {
      return { headline: "보유 종목을 추가하면 매일의 변화를 정리해드려요.", sub: null, tone: "empty" };
    }
    const withReport = holdings.filter(h => reports[h.symbol]);
    if (withReport.length === 0) {
      return { headline: "리포트가 준비되면 오늘의 흐름을 정리해드려요.", sub: null, tone: "empty" };
    }

    const total = withReport.length;
    // 현재 상태(state_stock) 기준으로 분류 — "어제 대비 변화"가 아니라 "지금 상태"
    const checkNames = withReport
      .filter(h => _normState(reports[h.symbol]?.state_stock) === "CHECKNEEDED")
      .map(h => h.name);
    const volNames = withReport
      .filter(h => _normState(reports[h.symbol]?.state_stock) === "VOLATILE")
      .map(h => h.name);
    const changeNames = withReport
      .filter(h => _normState(reports[h.symbol]?.state_stock) === "CHANGE")
      .map(h => h.name);

    const nameList = (arr) => arr.length <= 2 ? arr.join(", ") : `${arr[0]} 외 ${arr.length - 1}개`;

    // 우선순위: 새 정보 > 변동 큼 > 변화 발생 > 평온
    if (checkNames.length > 0) {
      const rest = total - checkNames.length;
      return {
        headline: `${nameList(checkNames)}에 새로 확인할 정보가 들어왔어요.`,
        sub: rest > 0 ? `나머지 ${rest}개 종목은 평소와 비슷한 흐름이에요.` : null,
        tone: "check",
      };
    }
    if (volNames.length > 0) {
      const rest = total - volNames.length;
      return {
        headline: `${nameList(volNames)}의 가격 움직임이 평소보다 큰 편이에요.`,
        sub: rest > 0 ? `나머지 ${rest}개 종목은 평소와 비슷한 흐름이에요.` : null,
        tone: "volatile",
      };
    }
    if (changeNames.length > 0) {
      const rest = total - changeNames.length;
      return {
        headline: `${nameList(changeNames)}에서 평소와 다른 움직임이 이어지고 있어요.`,
        sub: rest > 0 ? `나머지 ${rest}개 종목은 평소와 비슷한 흐름이에요.` : null,
        tone: "change",
      };
    }
    return {
      headline: "오늘 보유 종목들은 대체로 평소와 비슷한 흐름이에요.",
      sub: `${total}개 종목 모두 큰 변화 없이 이어지고 있어요.`,
      tone: "stable",
    };
  };
  const overview = getOverview();

  // 종합 해설 톤 → 색점 색상 (theme 토큰 재사용, 새 색 추가 안 함)
  const OVERVIEW_DOT = {
    stable: T.green, change: T.yellow, volatile: T.red, check: T.blue, empty: T.textMuted,
  };

  // Sprint 13: "오늘 할 일 1개" + 체크포인트 리마인드 통합 (Sprint24: action 문구는 종합해설로 대체, checkpoint만 사용)
  const getTodayAction = () => {
    if (holdings.length === 0) return { action: "종목을 추가하고 Pause 분석을 받아보세요.", detail: null, type: "empty" };

    // 1. 가장 주의가 필요한 종목 찾기 (CAUTION > PAUSE > WATCH > HOLD)
    const priority = { VOLATILE: 4, CHECKNEEDED: 3, CHANGE: 2, STABLE: 1, CAUTION: 4, PAUSE: 3, WATCH: 2, HOLD: 1 };
    const ranked = holdings
      .map(h => ({ h, r: reports[h.symbol], jc: h.journey_cache || {} }))
      .filter(x => x.r)
      .sort((a, b) => (priority[b.r.state_stock] || 0) - (priority[a.r.state_stock] || 0) || (b.jc.change_score || 0) - (a.jc.change_score || 0));

    const top = ranked[0];
    const restCount = holdings.length - 1;

    // 2. 체크포인트 리마인드 (전략별 세분화 + 스팸 방지)
    const periodConfig = {
      long_term:  { days: 180, midPct: 0.6, overPct: 1.2 },
      mid_term:   { days: 45,  midPct: 0.5, overPct: 1.1 },
      swing:      { days: 14,  midPct: 0.4, overPct: 1.0 }
    };
    let checkpoint = null;
    for (const { h, r, jc } of ranked) {
      const cfg = periodConfig[h.prefs?.investment_period] || periodConfig.long_term;
      const elapsed = jc.days_since_add || 0;
      if (elapsed <= 0) continue; // 방금 등록한 종목 제외
      const pnl = r?.payload?.micro?.pnl_pct;
      const midDay = Math.floor(cfg.days * cfg.midPct);
      const overDay = Math.floor(cfg.days * cfg.overPct);

      // 특정 시점 ±1일만 노출 (매일 뜨면 잔소리꾼)
      const isNearMid = elapsed >= midDay - 1 && elapsed <= midDay + 1 && elapsed < cfg.days;
      const isNearOver = elapsed >= overDay - 1 && elapsed <= overDay + 1;
      const isExceeded = elapsed >= cfg.days && !isNearOver;

      if (isNearMid) {
        checkpoint = { name: h.name, elapsed, target: cfg.days, period: h.prefs?.investment_period, pnl };
        break;
      }
      if (isNearOver || (isExceeded && elapsed === cfg.days)) {
        checkpoint = { name: h.name, elapsed, target: cfg.days, period: h.prefs?.investment_period, pnl, exceeded: true };
        break;
      }
    }

    // 3. 오늘 할 일 문장 생성
    if (!top?.r) return { action: "리포트가 아직 생성되지 않았습니다. 리포트 생성 버튼을 눌러주세요.", detail: null, type: "no_report" };

    const topState = top.r.state_stock;
    const topName = top.h.name;

    // 상태 변화 있는 종목
    const stateChanged = ranked.filter(x => x.r.payload?.state_changed);
    if (stateChanged.length > 0) {
      const changed = stateChanged[0];
      return {
        action: `${changed.h.name}에 변화가 생겼습니다. 가볍게 한 번만 확인해보세요.`,
        detail: restCount > 0 ? `나머지 ${restCount}개 종목은 변화 없이 안정적입니다.` : null,
        type: "changed", checkpoint
      };
    }

    // VOLATILE/CHECKNEEDED 종목 있음
    if (counts.VOLATILE > 0 || counts.CHECKNEEDED > 0) {
      // 전부 VOLATILE/CHECKNEEDED
      const alertCount = counts.VOLATILE + counts.CHECKNEEDED;
      if (alertCount === holdings.length) {
        return {
          action: `${holdings.length}개 모두 점검 구간입니다. 변동성 큰 종목부터 확인하면 충분합니다.`,
          detail: null, type: "caution", checkpoint
        };
      }
      if (counts.VOLATILE > 0) {
        return {
          action: restCount > 0
            ? `오늘은 ${topName}만 가볍게 확인하면 충분합니다.`
            : `오늘은 ${topName}만 가볍게 확인하면 충분합니다.`,
          detail: restCount > 0 ? `나머지 ${restCount}개는 전략 범위 안입니다.` : null,
          type: "caution", checkpoint
        };
      }
      return {
        action: "지금은 무리하게 움직이기보다 관망하는 쪽이 안전합니다.",
        detail: null, type: "pause", checkpoint
      };
    }

    // 전부 안정
    return {
      action: "오늘은 그대로 두는 게 가장 합리적입니다.",
      detail: `${holdings.length}개 종목 모두 전략 범위 안입니다.`,
      type: "stable", checkpoint
    };
  };

  const todayAction = getTodayAction();

  // Portfolio calculation (Hero에서 사용하기 위해 return 바깥으로 이동)
  let totalEval = 0, totalCost = 0;
  holdings.forEach(h => {
    const r = reports[h.symbol]; const pnl = r?.micro?.pnl_pct;
    const cp = r?.micro?.close || (pnl != null && h.avg_price ? Math.round(h.avg_price * (1 + pnl / 100)) : 0);
    totalEval += cp * (h.qty || 0);
    totalCost += (h.avg_price || 0) * (h.qty || 0);
  });
  const totalPnl = totalEval - totalCost;
  const totalPnlPct = totalCost > 0 ? ((totalPnl / totalCost) * 100).toFixed(1) : 0;

  return (
    <div>
      {/* ═══ 자동 리포트 생성 모달 ═══ */}
      {reportCreating && (
        <div style={{ position: "fixed", inset: 0, zIndex: 10000, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ background: T.card, border: `1px solid ${T.accent}40`, borderRadius: 20, padding: "36px 32px", maxWidth: 400, width: "100%", textAlign: "center" }}>
            {!reportCreating.done ? (
              <>
                <div style={{ fontSize: 36, marginBottom: 16 }}>📊</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: T.text, marginBottom: 8 }}>{reportCreating.name}</div>
                <div style={{ fontSize: 14, color: T.accent, fontWeight: 600, marginBottom: 16 }}>리포트 생성 중...</div>
                <div style={{ fontSize: 13, color: T.textDim, lineHeight: 1.7 }}>
                  공개 데이터를 수집하고 분석하고 있어요.<br />잠시만 기다려주세요.
                </div>
                <div style={{ marginTop: 20 }}>
                  <div style={{ width: 40, height: 40, border: `3px solid ${T.accent}30`, borderTop: `3px solid ${T.accent}`, borderRadius: "50%", animation: "spin 1s linear infinite", margin: "0 auto" }} />
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 36, marginBottom: 16 }}>✅</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: T.text, marginBottom: 8 }}>{reportCreating.name}</div>
                <div style={{ fontSize: 14, color: T.accent, fontWeight: 600, marginBottom: 20 }}>리포트가 준비되었어요!</div>
                <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
                  <button onClick={() => {
                    const h = holdings.find(x => x.symbol === reportCreating.symbol);
                    const r = reports[reportCreating.symbol];
                    setReportCreating(null);
                    if (h) goDetail(h, r);
                  }} style={{ background: T.accent, color: "#fff", border: "none", borderRadius: 10, padding: "12px 24px", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                    상세 분석 보기
                  </button>
                  <button onClick={() => {
                    const h = holdings.find(x => x.symbol === reportCreating.symbol);
                    setReportCreating(null);
                    if (h) goQA(h);
                  }} style={{ background: "transparent", color: T.accent, border: `1px solid ${T.accent}40`, borderRadius: 10, padding: "12px 24px", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                    AI 질문하기
                  </button>
                </div>
                <button onClick={() => setReportCreating(null)} style={{ background: "none", border: "none", color: T.textMuted, fontSize: 12, marginTop: 16, cursor: "pointer", fontFamily: "inherit" }}>
                  나중에 확인할게요
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {actionModal && <ActionModal holding={actionModal.holding} report={actionModal.report} defaultAction={actionModal.defaultAction} onClose={() => setActionModal(null)} onSave={async (data) => {
        saveAction(data);
        const remainQty = (actionModal.holding.qty || 0) - (data.sell_qty || 0);
        if (remainQty <= 0) {
          // 전량 매도 → 종목 삭제
          await del(actionModal.holding.id, true);
        } else {
          // 분할 매도 → 남은 수량으로 업데이트
          try {
            await api(`/holdings/${actionModal.holding.id}`, {
              method: "PUT",
              body: JSON.stringify({ qty: remainQty })
            }, token);
            load();
          } catch (e) {
            // PUT 미지원 시 삭제 후 재등록
            await del(actionModal.holding.id, true);
            try {
              await api("/holdings", {
                method: "POST",
                body: JSON.stringify({
                  symbol: actionModal.holding.symbol,
                  name: actionModal.holding.name,
                  avg_price: actionModal.holding.avg_price,
                  qty: remainQty
                })
              }, token);
              load();
            } catch (e2) { console.error(e2); }
          }
        }
        setActionModal(null);
      }} />}

      {/* ═══ PWA 설치 유도 배너 ═══ */}
      <InstallBanner />

      {/* ═══ 푸시 알림 설정 배너 ═══ */}
      {/* <PushNotificationBanner token={token} /> */}

      {/* ═══ Level 1: 오늘의 종합 해설 (Hero 최상단, 카드 없음) ═══ */}
      {holdings.length > 0 && (
        <div style={{ padding: "32px 4px 28px" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <span style={{
              width: 9, height: 9, borderRadius: "50%", flexShrink: 0, marginTop: 11,
              background: OVERVIEW_DOT[overview.tone] || T.textMuted,
            }} />
            <div>
              <div style={{ fontSize: 24, fontWeight: 700, color: T.text, lineHeight: 1.4, letterSpacing: "-0.01em" }}>
                {overview.headline}
              </div>
              {overview.sub && (
                <div style={{ fontSize: 15, color: T.textDim, lineHeight: 1.5, marginTop: 8 }}>
                  {overview.sub}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ═══ Hero: 내 자산 + 상태 (Bento Grid) ═══ */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16, marginBottom: 16 }}>
        {/* 좌: 포트폴리오 총합 */}
        <Card variant="hero">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>총 평가금</div>
            <PieChart size={16} color={T.textMuted} strokeWidth={1.5} />
          </div>
          <div className="mono-num" style={{ fontSize: 28, fontWeight: 700, color: T.text, marginBottom: 16 }}>
            {totalEval.toLocaleString()}<span style={{ fontSize: 14, color: T.textDim, fontWeight: 500, marginLeft: 4 }}>원</span>
          </div>
          <div style={{ display: "flex", gap: 24, paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 4 }}>총 손익</div>
              <div className="mono-num" style={{ fontSize: 16, fontWeight: 700, color: totalPnl >= 0 ? T.green : T.red }}>{totalPnl >= 0 ? "+" : ""}{totalPnl.toLocaleString()}원</div>
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 4 }}>수익률</div>
              <div className="mono-num" style={{ fontSize: 16, fontWeight: 700, color: +totalPnlPct >= 0 ? T.green : T.red }}>{+totalPnlPct >= 0 ? "+" : ""}{totalPnlPct}%</div>
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 4 }}>종목 수</div>
              <div className="mono-num" style={{ fontSize: 16, fontWeight: 700, color: T.text }}>{holdings.length}개</div>
            </div>
          </div>
        </Card>

        {/* 우: 상태 분포 + 스탠스 */}
        <Card variant="hero">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>상태별 분포</div>
            {streak > 1 && <span style={{ fontSize: 11, color: T.accent, fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}><Flame size={14} /> {streak}일 연속</span>}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
            {Object.entries(STATE).map(([k, v]) => {
              const stateDesc = { STABLE: "주요 지표 안정", CHANGE: "일부 변화 감지", VOLATILE: "변동폭 확대", CHECKNEEDED: "데이터 점검 필요" };
              return (
                <div key={k} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: v.color }} />
                    <span style={{ fontSize: 13, color: T.text, fontWeight: 600 }}>{v.label}</span>
                    <span style={{ fontSize: 11, color: T.textMuted }}>{stateDesc[k]}</span>
                  </div>
                  <span className="mono-num" style={{ fontSize: 16, fontWeight: 700, color: (counts[k] || 0) > 0 ? T.text : T.textMuted }}>{counts[k] || 0}</span>
                </div>
              );
            })}
          </div>
          <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 12, fontSize: 13, color: T.textDim, lineHeight: 1.5 }}>
            {counts.STABLE === holdings.length
              ? `${holdings.length}개 종목 모두 변화 낮음 구간이에요.`
              : `변화 낮음 ${counts.STABLE} · 변화 발생 ${counts.CHANGE} · 변동성 확대 ${counts.VOLATILE} · 점검 필요 ${counts.CHECKNEEDED}`}
          </div>
        </Card>
      </div>

      {/* 체크포인트 리마인드 (Hero에서 분리) */}
      {todayAction.checkpoint && (
        <Card style={{ marginBottom: 16, padding: "14px 18px" }}>
          <div style={{ fontSize: 13, color: T.textDim, display: "flex", alignItems: "center", gap: 8 }}>
            <Target size={14} color={T.accent} strokeWidth={1.5} />
            <span>
              <strong style={{ color: T.text }}>{todayAction.checkpoint.name}</strong>
              {" "}매수 <span className="mono-num">{todayAction.checkpoint.elapsed}</span>일째
              {todayAction.checkpoint.exceeded
                ? " · 전략 기간 초과, 점검 권장"
                : ` · 목표 ${todayAction.checkpoint.target}일 중 ${Math.round(todayAction.checkpoint.elapsed / todayAction.checkpoint.target * 100)}% 경과`
              }
              {todayAction.checkpoint.pnl != null && (
                <span className="mono-num" style={{ background: (todayAction.checkpoint.pnl >= 0 ? T.green : T.red) + "15", color: todayAction.checkpoint.pnl >= 0 ? T.green : T.red, fontWeight: 700, marginLeft: 6, padding: "2px 6px", borderRadius: 4, fontSize: 11 }}>
                  {todayAction.checkpoint.pnl >= 0 ? "+" : ""}{todayAction.checkpoint.pnl?.toFixed?.(1) ?? todayAction.checkpoint.pnl}%
                </span>
              )}
            </span>
          </div>
        </Card>
      )}

      {/* ═══ 2. 오늘의 브리핑 (나머지 변화 요약) ═══ */}
      {briefing && briefing.items && briefing.items.length > 0 && (
        <Card style={{ marginBottom: 16, borderLeft: `3px solid ${T.cyan}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: T.cyan }}>종목별 변화 요약</div>
          </div>
          {briefing.items.slice(0, 3).map((item, i) => {
            const imp = item.importance === "HIGH" ? { bg: T.red + "12", border: T.red + "25", color: T.red, icon: "high" }
              : item.importance === "MEDIUM" ? { bg: T.yellow + "12", border: T.yellow + "25", color: T.yellow, icon: "med" }
              : { bg: T.green + "06", border: T.green + "15", color: T.textDim, icon: "low" };
            return (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", marginBottom: i < briefing.items.length - 1 ? 6 : 0, borderRadius: 8, background: imp.bg, border: `1px solid ${imp.border}` }}>
                {imp.icon === "high" ? <AlertTriangle size={14} color={imp.color} /> : imp.icon === "med" ? <Info size={14} color={imp.color} /> : <CheckCircle size={14} color={imp.color} />}
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{item.name}</span>
                    <StateBadge state={item.state} />
                  </div>
                  <div style={{ fontSize: 13, color: T.textSub || T.textDim, lineHeight: 1.5 }}>
                    {(() => {
                      const headline = item.headline || "";
                      // 영어 상태 라벨 → 한글 치환 (백엔드 수정 전 임시)
                      const localized = headline
                        .replace(/\bSTABLE\b/g, "변화 낮음")
                        .replace(/\bCHANGE\b/g, "변화 발생")
                        .replace(/\bVOLATILE\b/g, "변동성 확대")
                        .replace(/\bCHECKNEEDED\b/g, "점검 필요")
                        .replace(/\bHOLD\b/g, "변화 낮음")
                        .replace(/\bWATCH\b/g, "변화 발생")
                        .replace(/\bCAUTION\b/g, "변동성 확대")
                        .replace(/\bPAUSE\b/g, "점검 필요");
                      return item.riskDelta != null && item.riskDelta !== 0
                        ? `Risk ${item.riskDelta > 0 ? "+" : ""}${item.riskDelta} · ${localized}`
                        : localized;
                    })()}
                  </div>
                </div>
              </div>
            );
          })}
        </Card>
      )}

      {/* (상태 카운트는 Hero에 통합됨) */}

      {/* ═══ 마이크로 피드백 배너 (상단, 하루 1회) ═══ */}
      {!feedbackDismissed && feedbackVisible && (
        <div style={{
          marginBottom: 16, padding: "12px 18px", background: T.card, borderRadius: 12,
          border: `1px solid ${T.accent}25`, borderLeft: `3px solid ${T.accent}`,
          display: "flex", justifyContent: "space-between", alignItems: "center",
          opacity: 1, transition: "opacity 0.5s ease",
        }}>
          <div style={{ fontSize: 13, color: T.text, lineHeight: 1.5 }}>
            어제 관망 유지한 종목이 오늘 하락 구간에 들어갔습니다. 관망 결정이 결과적으로 더 보수적인 선택이었습니다.
          </div>
          <button onClick={() => { setFeedbackDismissed(true); setFeedbackVisible(false); localStorage.setItem(feedbackDismissedKey, "true"); }}
            style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", padding: 4, fontSize: 16, lineHeight: 1 }}>×</button>
        </div>
      )}

      {/* ═══ 최근 행동 복기 카드 (최대 2개) ═══ */}
      {holdings.length > 0 && (() => {
        const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
        const recentHolds = holdings.filter(h => localStorage.getItem(`pause_active_hold_${h.symbol}_${yesterday}`));
        if (recentHolds.length === 0) return null;

        return (
          <Card style={{ marginBottom: 16, borderLeft: `3px solid ${T.accent}` }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.accent, marginBottom: 12 }}>최근 행동 복기</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {recentHolds.slice(0, 2).map((h, i) => {
                const r = reports[h.symbol];
                const pnl1d = r?.micro?.pnl_1d;
                const isDefensive = pnl1d != null && pnl1d < 0;
                return (
                  <div key={i} style={{ padding: "12px 14px", background: T.bg, borderRadius: 10, border: `1px solid ${T.border}` }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{h.name}</span>
                      <span className="mono-num" style={{ fontSize: 12, color: T.textMuted }}>{yesterday}</span>
                    </div>
                    <div style={{ fontSize: 13, color: T.textDim, lineHeight: 1.5 }}>
                      어제 관망을 유지했습니다.
                      {isDefensive
                        ? ` 이후 ${Math.abs(pnl1d).toFixed(1)}% 하락 구간이 이어졌습니다. 관망이 더 보수적인 선택이었습니다.`
                        : pnl1d != null ? ` 이후 ${pnl1d > 0 ? "+" : ""}${pnl1d.toFixed(1)}% 변화가 있었습니다.` : ""
                      }
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        );
      })()}

      {/* ═══ 원칙 이탈 현황 (Sprint 21) ═══ */}
      {holdings.length > 0 && (
        <PrincipleDriftCard token={token} onGoDetail={(code) => {
          const h = holdings.find(h => h.symbol === code);
          if (h) goDetail(h);
        }} />
      )}

      {/* ═══ 포트폴리오 비중 (총합은 Hero에 통합됨) ═══ */}
      {holdings.length > 0 && (() => {
        return (
          <Card style={{ marginBottom: 20 }}>

            {/* Portfolio Weight Bar - 종목별 투자 비중 */}
            {(() => {
              return (
                <>
                  <div style={{ fontSize: 12, fontWeight: 700, color: T.textDim, marginBottom: 8 }}>종목별 투자 비중</div>
                  <div style={{ display: "flex", gap: 2, marginBottom: 8, borderRadius: 6, overflow: "hidden" }}>
                    {holdings.map((h, idx) => {
                      const hCost = (h.avg_price || 0) * (h.qty || 0);
                      const pct = totalCost > 0 ? (hCost / totalCost * 100) : 0;
                      const color = STOCK_COLORS[idx % STOCK_COLORS.length];
                      return pct > 0 ? (
                        <div key={h.id} style={{ width: `${pct}%`, height: 12, background: color, minWidth: 4, transition: "width 0.5s" }}
                          title={`${h.name}: ${pct.toFixed(1)}% (${hCost.toLocaleString()}원)`} />
                      ) : null;
                    })}
                  </div>
                  <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                    {holdings.map((h, idx) => {
                      const hCost = (h.avg_price || 0) * (h.qty || 0);
                      const pct = totalCost > 0 ? (hCost / totalCost * 100).toFixed(1) : 0;
                      const color = STOCK_COLORS[idx % STOCK_COLORS.length];
                      return (
                        <div key={h.id} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11 }}>
                          <div style={{ width: 8, height: 8, borderRadius: 2, background: color }} />
                          <span style={{ color: T.textDim }}>{h.name}</span>
                          <span style={{ color, fontWeight: 700 }}>{pct}%</span>
                        </div>
                      );
                    })}
                  </div>
                </>
              );
            })()}
          </Card>
        );
      })()}

      {/* ═══ 5. 투자 건강 점수 + C3: 상관관계 경고 ═══ */}
      {holdings.length >= 2 && (canAccess(plan, "health_score") ? (() => {
        // 건강 점수 간이 계산 (프론트 버전)
        const count = holdings.length;
        let diversityScore = count >= 5 ? 20 : count >= 3 ? 15 : count >= 2 ? 10 : 5;
        const totalCost = holdings.reduce((s, h) => s + (h.avg_price || 0) * (h.qty || 0), 0);
        const maxWeight = Math.max(...holdings.map(h => totalCost > 0 ? ((h.avg_price || 0) * (h.qty || 0)) / totalCost * 100 : 0));
        if (maxWeight > 80) diversityScore = Math.max(diversityScore - 7, 2);
        else if (maxWeight > 60) diversityScore = Math.max(diversityScore - 3, 3);

        let stabilityScore = 25;
        const states = holdings.map(h => reports[h.symbol]?.state_stock).filter(Boolean);
        const cautionCount = states.filter(s => s === "CAUTION" || s === "PAUSE" || s === "VOLATILE" || s === "CHECKNEEDED").length;
        if (states.length > 0) {
          // CHECKNEEDED/PAUSE는 VOLATILE/CAUTION보다 덜 위험하게 (0.5 가중치)
          const pauseCount = states.filter(s => s === "PAUSE" || s === "CHECKNEEDED").length;
          const realCaution = states.filter(s => s === "CAUTION" || s === "VOLATILE").length;
          const weightedBad = realCaution + pauseCount * 0.5;
          stabilityScore = Math.round(Math.max(5, (1 - weightedBad / states.length) * 25));
        }

        let riskScore = 20;
        const risks = holdings.map(h => reports[h.symbol]?.payload?.risk).filter(r => r != null);
        if (risks.length > 0) riskScore = Math.round(Math.max(0, 25 - (risks.reduce((s, v) => s + v, 0) / risks.length / 100) * 25));

        const alignScore = 15;
        const totalScore = diversityScore + stabilityScore + alignScore + riskScore;
        const grade = totalScore >= 80 ? "매우 건강" : totalScore >= 60 ? "건강" : totalScore >= 40 ? "보통" : "위험";
        const gradeColor = totalScore >= 80 ? T.green : totalScore >= 60 ? T.cyan : totalScore >= 40 ? T.yellow : T.red;

        // 비중 쏠림 경고
        const warnings = [];
        if (maxWeight > 70) warnings.push(`한 종목에 ${maxWeight.toFixed(0)}% 집중되어 있습니다. 분산이 필요합니다.`);
        if (cautionCount >= 2) warnings.push(`주의/보류 종목이 ${cautionCount}개입니다. 리스크를 점검하세요.`);

        // 같은 업종 쏠림 체크
        const sectorMap = {};
        holdings.forEach(h => {
          const sn = reports[h.symbol]?.micro?.sector_name;
          if (sn) sectorMap[sn] = (sectorMap[sn] || 0) + 1;
        });
        Object.entries(sectorMap).forEach(([name, cnt]) => {
          if (cnt >= 2) warnings.push(`${name} 업종에 ${cnt}개 종목이 집중되어 있습니다. 동반 하락 가능성이 있습니다.`);
        });

        return (
          <Card style={{ marginBottom: 20, borderLeft: `4px solid ${gradeColor}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 16 }}>
              <SectionTitle icon="Activity" tip="포트폴리오의 분산, 안정성, 리스크를 종합 평가한 점수입니다. 점수가 높을수록 균형 잡힌 상태입니다.">투자 건강 점수</SectionTitle>
              <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                <span className="mono-num" style={{ fontSize: 32, fontWeight: 700, color: gradeColor, lineHeight: 1 }}>{totalScore}</span>
                <span className="mono-num" style={{ fontSize: 14, color: T.textMuted, fontWeight: 600 }}>/ 100</span>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
              <div style={{ flex: 1, height: 6, background: T.bg, borderRadius: 3, overflow: "hidden" }}>
                <div style={{ width: `${totalScore}%`, height: "100%", background: gradeColor, borderRadius: 3, transition: "width 0.5s" }} />
              </div>
              <span style={{ fontSize: 13, fontWeight: 700, color: gradeColor }}>{grade}</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: warnings.length > 0 ? 16 : 0 }}>
              {[
                { label: "분산도", score: diversityScore, max: 25 },
                { label: "안정도", score: stabilityScore, max: 25 },
                { label: "행동 일치", score: alignScore, max: 25 },
                { label: "리스크", score: riskScore, max: 25 },
              ].map((item, i) => (
                <div key={i} style={{ padding: "12px 14px", background: T.bg, borderRadius: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ fontSize: 13, color: T.textDim, fontWeight: 600 }}>{item.label}</div>
                  <div className="mono-num" style={{ display: "flex", alignItems: "baseline", gap: 2 }}>
                    <span style={{ fontSize: 18, fontWeight: 700, color: item.score >= item.max * 0.7 ? T.green : item.score >= item.max * 0.4 ? T.yellow : T.red }}>{item.score}</span>
                    <span style={{ fontSize: 11, color: T.textMuted }}>/{item.max}</span>
                  </div>
                </div>
              ))}
            </div>
            {warnings.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {warnings.map((w, i) => (
                  <div key={i} style={{ fontSize: 13, color: T.yellow, padding: "10px 14px", background: T.yellow + "08", border: `1px solid ${T.yellow}15`, borderRadius: 8, display: "flex", gap: 8, alignItems: "center", lineHeight: 1.5 }}>
                    <AlertTriangle size={14} color={T.yellow} style={{ flexShrink: 0 }} /> {w}
                  </div>
                ))}
              </div>
            )}
          </Card>
        );
      })() : (
        <Card style={{ marginBottom: 20, cursor: "pointer" }} onClick={() => onUpgrade?.("health_score")}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0" }}>
            <Lock size={24} color={T.textMuted} />
            <div>
              <div style={{ fontSize: 14, fontWeight: 800, color: T.text }}>투자 건강 점수</div>
              <div style={{ fontSize: 12, color: T.textMuted }}>Core 이상 플랜에서 포트폴리오 건강 점수를 확인할 수 있습니다</div>
            </div>
            <span style={{ fontSize: 10, padding: "4px 10px", background: T.accent + "20", color: T.accent, borderRadius: 4, fontWeight: 700, marginLeft: "auto" }}>CORE</span>
          </div>
        </Card>
      ))}

      {/* ═══ 6. 섹터 로테이션 — 업종별 자금 흐름 (시장 배경 = 하단) ═══ */}
      {canAccess(plan, "sector_rotation") ? (() => {
        // 모든 리포트에서 macro 가져오기
        const anyReport = Object.values(reports)[0];
        const macro = anyReport?.macro;
        if (!macro?.sector_top3?.length) return null;
        const marketTemp = anyReport?.state_market;
        const mt = MARKET[marketTemp] || MARKET.NEUTRAL;
        return (
          <Card style={{ marginBottom: 20, borderLeft: `3px solid ${T.cyan}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <SectionTitle tip="오늘 어떤 업종에 돈이 들어가고 빠지는지 보여줍니다. 내 종목이 속한 업종의 분위기를 파악할 수 있습니다" badge="PRO">섹터 로테이션</SectionTitle>
              <span style={{ fontSize: 11, color: mt.color, fontWeight: 700 }}>시장 {mt.label}</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <div style={{ fontSize: 11, color: T.green, fontWeight: 700, marginBottom: 8 }}>상승 업종</div>
                {macro.sector_top3.map((s, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 10px", background: T.bg, borderRadius: 6, marginBottom: 4 }}>
                    <span style={{ fontSize: 12, color: T.text, fontWeight: 600 }}>{i + 1}. {s.name}</span>
                    <span style={{ fontSize: 12, color: T.green, fontWeight: 800 }}>+{s.change}%</span>
                  </div>
                ))}
              </div>
              <div>
                <div style={{ fontSize: 11, color: T.red, fontWeight: 700, marginBottom: 8 }}>하락 업종</div>
                {macro.sector_bottom3.map((s, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 10px", background: T.bg, borderRadius: 6, marginBottom: 4 }}>
                    <span style={{ fontSize: 12, color: T.text, fontWeight: 600 }}>{i + 1}. {s.name}</span>
                    <span style={{ fontSize: 12, color: T.red, fontWeight: 800 }}>{s.change}%</span>
                  </div>
                ))}
              </div>
            </div>
            {/* 내 종목 업종 표시 */}
            {(() => {
              const mySet = new Set(holdings.map(h => reports[h.symbol]?.micro?.sector_name).filter(Boolean));
              const inTop = macro.sector_top3.filter(s => mySet.has(s.name));
              const inBottom = macro.sector_bottom3.filter(s => mySet.has(s.name));
              if (inTop.length === 0 && inBottom.length === 0) return null;
              return (
                <div style={{ marginTop: 10, padding: "8px 12px", background: T.bg, borderRadius: 8, fontSize: 12 }}>
                  {inTop.length > 0 && <div style={{ color: T.green }}>내 종목 업종 중 <strong>{inTop.map(s => s.name).join(", ")}</strong>이(가) 오늘 상승 업종입니다.</div>}
                  {inBottom.length > 0 && <div style={{ color: T.red, marginTop: 4 }}>내 종목 업종 중 <strong>{inBottom.map(s => s.name).join(", ")}</strong>이(가) 오늘 하락 업종입니다. 주의가 필요합니다.</div>}
                </div>
              );
            })()}
          </Card>
        );
      })() : (
        <Card style={{ marginBottom: 20, cursor: "pointer" }} onClick={() => onUpgrade?.("sector_rotation")}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0" }}>
            <Lock size={24} color={T.textMuted} />
            <div>
              <div style={{ fontSize: 14, fontWeight: 800, color: T.text }}>섹터 로테이션</div>
              <div style={{ fontSize: 12, color: T.textMuted }}>Core 이상 플랜에서 업종별 자금 흐름을 확인할 수 있습니다</div>
            </div>
            <span style={{ fontSize: 10, padding: "4px 10px", background: T.accent + "20", color: T.accent, borderRadius: 4, fontWeight: 700, marginLeft: "auto" }}>CORE</span>
          </div>
        </Card>
      )}

      {/* (브리핑은 상단으로 이동됨) */}

      <div className="phase-actions" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: T.text, margin: 0 }}>내 포트폴리오</h2>
          <p style={{ fontSize: 12, color: T.textDim, margin: "4px 0 0" }}>
            보유 {holdings.length}개 · 대기 {watchlist.length}개
            {(() => {
              const anyReport = Object.values(reports)[0];
              const mt = anyReport?.state_market;
              if (!mt) return null;
              const m = MARKET[mt] || MARKET.NEUTRAL;
              return <span style={{ marginLeft: 8, color: m.color, fontSize: 11 }}>· 시장 {m.label}</span>;
            })()}
          </p>
        </div>
        <div className="phase-action-btns" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Btn variant="ghost" onClick={goJourney}>투자 여정</Btn>
          <Btn variant="ghost" onClick={goMonthly}>월간 리포트</Btn>
          <Btn variant="ghost" onClick={goDividend}>배당 캘린더</Btn>
          <Btn variant="ghost" onClick={generate} disabled={generating}>{generating ? "리포트 생성 중" : "전체 분석"}</Btn>
          <Btn variant="ghost" style={{ borderColor: T.cyan + "40", color: T.cyan }} onClick={goMarketQA}>시장 질문</Btn>
          <Btn variant="primary" onClick={() => setAdding(!adding)}>{adding ? "취소" : "종목 추가"}</Btn>
        </div>
      </div>

      {/* ═══ 필터 탭 (전체/보유/대기) ═══ */}
      {(holdings.length > 0 || watchlist.length > 0) && (
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {[
            { key: "all", label: `전체 ${holdings.length + watchlist.length}` },
            { key: "holding", label: `보유 ${holdings.length}` },
            { key: "watchlist", label: `대기 ${watchlist.length}` },
          ].map(tab => (
            <button key={tab.key} onClick={() => setStockFilter(tab.key)} style={{
              padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600,
              background: stockFilter === tab.key ? T.accent + "20" : "transparent",
              color: stockFilter === tab.key ? T.accent : T.textDim,
              border: `1px solid ${stockFilter === tab.key ? T.accent + "40" : T.border}`,
              cursor: "pointer", transition: "all 0.15s", fontFamily: "inherit",
            }}>{tab.label}</button>
          ))}
        </div>
      )}

      {adding && (
        <Card style={{ marginBottom: 20, textAlign: "left" }}>
          {/* 보유/대기 선택 탭 */}
          <div style={{ display: "flex", gap: 0, marginBottom: 16, borderRadius: 10, overflow: "hidden", border: `1px solid ${T.border}` }}>
            <button onClick={() => setAddMode("holding")} style={{
              flex: 1, padding: "10px 0", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
              background: addMode === "holding" ? T.accent : "transparent",
              color: addMode === "holding" ? "#fff" : T.textDim, border: "none",
            }}>이미 보유 중</button>
            <button onClick={() => setAddMode("watchlist")} style={{
              flex: 1, padding: "10px 0", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
              background: addMode === "watchlist" ? T.accent : "transparent",
              color: addMode === "watchlist" ? "#fff" : T.textDim, border: "none",
            }}>지켜보는 중</button>
          </div>

          {addMode === "holding" ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 400 }}>
              <StockSearch onSelect={(code, name) => setForm({ ...form, symbol: code, name })} />
              <div>
                <div style={{ fontSize: 13, color: T.textSub || T.textDim, fontWeight: 600, marginBottom: 6 }}>평균단가 (내가 산 가격)</div>
                <Input placeholder="70000" type="number" value={form.avg_price} onChange={e => setForm({ ...form, avg_price: e.target.value })} />
              </div>
              <div>
                <div style={{ fontSize: 13, color: T.textSub || T.textDim, fontWeight: 600, marginBottom: 6 }}>수량 (몇 주)</div>
                <Input placeholder="10" type="number" value={form.qty} onChange={e => setForm({ ...form, qty: e.target.value })} />
              </div>
              <div>
                <div style={{ fontSize: 13, color: T.textSub || T.textDim, fontWeight: 600, marginBottom: 6 }}>보유 기간</div>
                <select value={form.period} onChange={e => setForm({ ...form, period: e.target.value })} style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${T.border}`, background: T.bg, color: T.text, fontSize: 13, outline: "none", cursor: "pointer", appearance: "none", WebkitAppearance: "none" }}>
                  <option value="long_term">장기 (6개월 이상)</option>
                  <option value="mid_term">중장기 (1~6개월)</option>
                  <option value="swing">스윙 (1~4주)</option>
                </select>
              </div>
              <div>
                <div style={{ fontSize: 13, color: T.textSub || T.textDim, fontWeight: 600, marginBottom: 6 }}>투자 전략</div>
                <select value={form.strategy} onChange={e => setForm({ ...form, strategy: e.target.value })} style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${T.border}`, background: T.bg, color: T.text, fontSize: 13, outline: "none", cursor: "pointer", appearance: "none", WebkitAppearance: "none" }}>
                  <option value="general">일반 (특별한 전략 없음)</option>
                  <option value="dividend">배당 (배당금 수익 목적)</option>
                  <option value="value">가치 (저평가 종목 매수)</option>
                  <option value="growth">성장 (매출·시장 성장 기대)</option>
                </select>
              </div>
              <Btn variant="success" onClick={add}>등록</Btn>
              {form.symbol && <div style={{ fontSize: 12, color: T.accent, marginTop: 8 }}>선택됨: {form.name} ({form.symbol})</div>}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 400 }}>
              <StockSearch onSelect={(code, name) => setWatchForm({ ...watchForm, symbol: code, name })} />
              <div>
                <div style={{ fontSize: 13, color: T.textSub || T.textDim, fontWeight: 600, marginBottom: 6 }}>왜 이 종목을 보고 있나요? <span style={{ color: T.red, fontSize: 11 }}>필수</span></div>
                <textarea placeholder="예: 실적 턴어라운드 기대, 업종 대비 저평가" value={watchForm.watch_reason} onChange={e => setWatchForm({ ...watchForm, watch_reason: e.target.value })} rows={2} style={{ width: "100%", background: T.bg, color: T.text, border: `1px solid ${T.border}`, padding: "10px 14px", borderRadius: 10, fontSize: 13, fontFamily: "inherit", resize: "vertical", outline: "none", boxSizing: "border-box" }} />
              </div>
              <div>
                <div style={{ fontSize: 13, color: T.textSub || T.textDim, fontWeight: 600, marginBottom: 6 }}>진입 조건 (선택)</div>
                <textarea placeholder="예: 20일선 회복 시, 실적 발표 확인 후" value={watchForm.entry_conditions} onChange={e => setWatchForm({ ...watchForm, entry_conditions: e.target.value })} rows={2} style={{ width: "100%", background: T.bg, color: T.text, border: `1px solid ${T.border}`, padding: "10px 14px", borderRadius: 10, fontSize: 13, fontFamily: "inherit", resize: "vertical", outline: "none", boxSizing: "border-box" }} />
              </div>
              <div>
                <div style={{ fontSize: 13, color: T.textSub || T.textDim, fontWeight: 600, marginBottom: 6 }}>목표 매수가 (선택)</div>
                <Input placeholder="52000" type="number" value={watchForm.target_price} onChange={e => setWatchForm({ ...watchForm, target_price: e.target.value })} />
              </div>
              <Btn variant="success" onClick={async () => {
                if (!watchForm.symbol) return alert("종목을 검색해서 선택하세요");
                if (!watchForm.watch_reason.trim()) return alert("감시 이유를 입력해주세요");
                try {
                  await api("/watchlist", { method: "POST", body: JSON.stringify({
                    stock_code: watchForm.symbol, stock_name: watchForm.name,
                    watch_reason: watchForm.watch_reason.trim(),
                    entry_conditions: watchForm.entry_conditions.trim() || null,
                    target_price: watchForm.target_price ? parseFloat(watchForm.target_price) : null,
                  }) }, token);
                  setWatchForm({ symbol: "", name: "", watch_reason: "", entry_conditions: "", target_price: "" });
                  setAdding(false); load();
                } catch (e) { alert(e.message); }
              }}>대기종목 등록</Btn>
              {watchForm.symbol && <div style={{ fontSize: 12, color: T.accent, marginTop: 8 }}>선택됨: {watchForm.name} ({watchForm.symbol})</div>}
              <div style={{ fontSize: 11, color: T.textMuted, lineHeight: 1.5 }}>대기종목 등록은 매수 추천이 아닙니다. 진입 여부는 본인의 판단과 원칙에 따라 결정하세요.</div>
            </div>
          )}
        </Card>
      )}

      <div className="phase-stock-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 14 }}>
        {/* ═══ 보유종목 카드 ═══ */}
        {(stockFilter === "all" || stockFilter === "holding") && holdings.map((h, idx) => {
          const stockColor = STOCK_COLORS[idx % STOCK_COLORS.length];
          const r = reports[h.symbol]; const pnl = r?.micro?.pnl_pct;
          // 현재가: micro.close 또는 평단가 기반 역산
          const currentPrice = r?.micro?.close || (pnl != null && h.avg_price ? Math.round(h.avg_price * (1 + pnl / 100)) : 0);
          const totalValue = currentPrice * (h.qty || 0);
          const totalCost = (h.avg_price || 0) * (h.qty || 0);
          const pnlAmount = totalValue - totalCost;
          return (
            <Card key={h.id} hover onClick={() => goDetail(h, r)} style={{ borderTop: `3px solid ${stockColor}`, position: "relative" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: T.text }}>{h.name || h.symbol}</div>
                    {r ? <StateBadge state={r.state_stock} /> : <span style={{ fontSize: 11, color: T.textMuted }}>대기</span>}
                  </div>
                  <div className="mono-num" style={{ fontSize: 11, color: T.textMuted }}>{h.symbol} · {h.qty}주</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
                  {(h.prefs?.investment_period || h.prefs?.investment_style) && <span style={{ padding: "1px 6px", borderRadius: 4, fontSize: 10, fontWeight: 600, background: T.accent + "15", color: T.accent }}>{{ long_term: "장기", mid_term: "중장기", swing: "스윙" }[h.prefs.investment_period || h.prefs.investment_style] || "장기"}</span>}
                  {h.prefs?.investment_strategy && h.prefs.investment_strategy !== "general" && <span style={{ padding: "1px 6px", borderRadius: 4, fontSize: 10, fontWeight: 600, background: T.yellow + "15", color: T.yellow }}>{{ dividend: "배당", value: "가치", growth: "성장" }[h.prefs.investment_strategy]}</span>}
                </div>
              </div>

              {r && (
                <>
                  {/* 핵심 수치만: 현재가 + 수익률 */}
                  <div style={{ display: "flex", gap: 20, marginBottom: 12, padding: "12px 14px", background: T.bg, borderRadius: 10 }}>
                    <div>
                      <div className="label-micro" style={{ marginBottom: 4 }}>현재가</div>
                      <div className="mono-num" style={{ fontSize: 16, fontWeight: 700, color: T.text }}>{currentPrice.toLocaleString()}원</div>
                    </div>
                    <div>
                      <div className="label-micro" style={{ marginBottom: 4 }}>수익률</div>
                      <div className="mono-num" style={{ fontSize: 16, fontWeight: 700, color: pnl >= 0 ? T.green : T.red }}>{pnl >= 0 ? "+" : ""}{pnl?.toFixed(1)}%</div>
                    </div>
                  </div>

                  {/* 해석 1줄 — 변화 요약 */}
                  {(() => {
                    const jc = h.journey_cache || {};
                    let headline = jc.change_headline || r.reasons?.[0] || null;
                    if (!headline) return null;
                    headline = headline
                      .replace(/CHECKNEEDED/g, "정보 점검 필요")
                      .replace(/STABLE/g, "변화 낮음")
                      .replace(/CHANGE/g, "변화 발생")
                      .replace(/VOLATILE/g, "변동성 확대")
                      .replace(/HOLD/g, "변화 낮음")
                      .replace(/WATCH/g, "변화 발생")
                      .replace(/CAUTION/g, "변동성 확대")
                      .replace(/PAUSE/g, "정보 점검 필요");
                    const isHigh = (jc.change_score || 0) >= 100;
                    const isMed = (jc.change_score || 0) >= 30;
                    const clr = isHigh ? T.red : isMed ? T.yellow : T.textDim;
                    return (
                      <div style={{ fontSize: 13, color: clr, lineHeight: 1.5, marginBottom: 12 }}>
                        {headline}
                      </div>
                    );
                  })()}
                </>
              )}

              <div className="phase-card-btns" style={{ display: "flex", gap: 8 }}>
                {!r && (
                  <Btn variant="outline" style={{ flex: 1, padding: "10px 0", fontSize: 13, fontWeight: 700, borderRadius: 10, justifyContent: "center", borderColor: T.accent + "40", color: T.accent }}
                    onClick={async e => {
                      e.stopPropagation();
                      try {
                        await api("/test/report/am", { method: "POST" }, token);
                        await load();
                      } catch (err) { alert("리포트 생성 실패: " + err.message); }
                    }}>리포트 생성</Btn>
                )}
                <Btn variant="outline" style={{ flex: 1, padding: "10px 0", fontSize: 13, fontWeight: 700, borderRadius: 10, justifyContent: "center" }} onClick={e => { e.stopPropagation(); goDetail(h, r); }}>상세 보기</Btn>
                <Btn variant="primary" style={{ flex: 1, padding: "10px 0", fontSize: 13, fontWeight: 700, background: T.accent, color: "#fff", borderRadius: 10, justifyContent: "center" }} onClick={e => { e.stopPropagation(); goQA(h); }}>AI 상담</Btn>
              </div>
            </Card>
          );
        })}

        {/* ═══ 대기종목 카드 (Sprint 22) ═══ */}
        {(stockFilter === "all" || stockFilter === "watchlist") && watchlist.map((w) => {
          const wr = watchReports[w.stock_code];
          const currentPrice = wr?.micro?.close || 0;
          const targetPrice = w.target_price ? Number(w.target_price) : null;
          const targetGapPct = targetPrice && currentPrice > 0 ? ((currentPrice - targetPrice) / targetPrice * 100).toFixed(1) : null;
          const watchStatusMeta = {
            WATCHING: { label: "관망", color: T.textMuted },
            PREPARING: { label: "준비", color: T.yellow },
            APPROACHING: { label: "조건 근접", color: T.green },
            EXCLUDED: { label: "제외 고려", color: T.red },
          };
          const st = watchStatusMeta[w.status] || watchStatusMeta.WATCHING;
          return (
            <Card key={`watch-${w.stock_code}`} hover onClick={() => goDetail({ symbol: w.stock_code, name: w.stock_name, _watchlist: true, _watchEntry: w }, wr)} style={{ borderTop: `3px dashed ${T.textMuted}40`, position: "relative", opacity: 0.9 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: T.text }}>{w.stock_name}</div>
                    {wr ? <StateBadge state={wr.state_stock} /> : null}
                  </div>
                  <div className="mono-num" style={{ fontSize: 11, color: T.textMuted }}>{w.stock_code}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ padding: "1px 6px", borderRadius: 4, fontSize: 10, fontWeight: 600, background: st.color + "15", color: st.color }}>{st.label}</span>
                  <span style={{ padding: "1px 6px", borderRadius: 4, fontSize: 10, fontWeight: 600, background: T.textMuted + "15", color: T.textMuted }}>대기</span>
                </div>
              </div>

              {/* 대기종목 핵심: 현재가 + 목표가 거리 */}
              <div style={{ display: "flex", gap: 20, marginBottom: 12, padding: "12px 14px", background: T.bg, borderRadius: 10 }}>
                <div>
                  <div className="label-micro" style={{ marginBottom: 4 }}>현재가</div>
                  <div className="mono-num" style={{ fontSize: 16, fontWeight: 700, color: T.text }}>{currentPrice > 0 ? currentPrice.toLocaleString() + "원" : "—"}</div>
                </div>
                {targetGapPct !== null && (
                  <div>
                    <div className="label-micro" style={{ marginBottom: 4 }}>목표가 대비</div>
                    <div className="mono-num" style={{ fontSize: 16, fontWeight: 700, color: parseFloat(targetGapPct) <= 0 ? T.green : T.textDim }}>
                      {parseFloat(targetGapPct) > 0 ? "+" : ""}{targetGapPct}%
                    </div>
                  </div>
                )}
              </div>

              {/* 감시 이유 1줄 */}
              <div style={{ fontSize: 13, color: T.textDim, lineHeight: 1.5, marginBottom: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {w.watch_reason || "감시 이유 미입력"}
              </div>

              <div className="phase-card-btns" style={{ display: "flex", gap: 8 }}>
                <Btn variant="outline" style={{ flex: 1, padding: "10px 0", fontSize: 13, fontWeight: 700, borderRadius: 10, justifyContent: "center" }} onClick={e => { e.stopPropagation(); goDetail({ symbol: w.stock_code, name: w.stock_name, _watchlist: true, _watchEntry: w }, wr); }}>상세 보기</Btn>
                <Btn variant="primary" style={{ flex: 1, padding: "10px 0", fontSize: 13, fontWeight: 700, background: T.accent, color: "#fff", borderRadius: 10, justifyContent: "center" }} onClick={e => { e.stopPropagation(); goQA({ symbol: w.stock_code, name: w.stock_name, _watchlist: true }); }}>AI 상담</Btn>
              </div>
            </Card>
          );
        })}
      </div>

      {holdings.length === 0 && watchlist.length === 0 && (
        <div style={{ textAlign: "center", padding: 80, color: T.textDim }}>
          <Activity size={48} strokeWidth={1} color={T.border} style={{ marginBottom: 16 }} />
          <div style={{ fontSize: 15, marginBottom: 8 }}>관리 중인 종목이 없습니다</div>
          <div style={{ fontSize: 13, marginBottom: 16 }}>보유 종목을 등록하거나, 지켜볼 종목을 대기 등록해보세요</div>
          <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
            <Btn variant="primary" onClick={() => { setAddMode("holding"); setAdding(true); }}>보유 종목 등록</Btn>
            <Btn variant="outline" onClick={() => { setAddMode("watchlist"); setAdding(true); }}>대기 종목 등록</Btn>
          </div>
        </div>
      )}

      {/* ═══ 빈 상태 블록: 종목 있는데 리포트 없음 ═══ */}
      {holdings.length > 0 && Object.keys(reports).length === 0 && (
        <Card style={{ marginBottom: 20, borderLeft: `3px solid ${T.accent}` }}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 8 }}>
              등록하신 종목의 첫 번째 분석 리포트를 준비하고 있습니다.
            </div>
            <div style={{ fontSize: 13, color: T.textDim, lineHeight: 1.6 }}>
              리포트가 준비되면 대시보드에서 바로 확인할 수 있습니다.
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
            {holdings.slice(0, 3).map((h, i) => (
              <span key={i} style={{
                padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                color: T.textDim, border: `1px solid ${T.accent}25`, background: "transparent",
                animation: "pulse 3s ease-in-out infinite",
                animationDelay: `${i * 0.5}s`,
              }}>
                {h.name} <span className="mono-num" style={{ color: T.textMuted, marginLeft: 4 }}>{h.symbol}</span>
              </span>
            ))}
          </div>
          <div style={{ fontSize: 12, color: T.textMuted }}>
            평소처럼 투자를 진행해 주세요.
          </div>
        </Card>
      )}
    </div>
  );
}
